import { Step } from "./store-pyr";
import { SANDBOX_TOTAL } from './practice_pools';
import imgBravoNurro from './images/bravo-nurro.png';
// Amy's sandbox cell 648518346350730372 before and after she cut a fused
// axon off it (2026-09-26). Web-sized JPEGs; originals in her images.
import imgCutBefore from './images/cut-before.jpg';
import imgCutAfter from './images/cut-after.jpg';
// The second cut's cell, fused and after the cut (Ames, 2026-10-06).
import { beginPractice, endPractice, ensureTool, hasCutPreview, holdsSlot, openSplitView } from './practice';
import { useTutorialStore } from './store-pyr';
import { startTutorialButton, BLACK_BOX_NOTE, CHEAT_SHEET_URL, practiceStatus, MIDDLE, OVER_2D, OVER_3D, beforeAfter, celebrateStep, closeSidePanel, finishPracticeTutorial, getViewer, showWhereToCut, movingToSandbox, stopWatching, watchCutStep, watchPractice, watchTool } from './tutorial-3';

/**
 * The Cut track is staged (Ames, 2026-10-05): the same two cells in the same
 * order for everyone, a 3D cut first, then a 2D one. When a named cell is
 * not free the claim falls back to another, and with no second cell the 2D
 * step is skipped.
 */
const CUT_FIRST = '02c5adcc-23c8-4003-83cd-cd9df7a65ce0';  // Fusion on a proofread cell (3D)
const CUT_SECOND = '0813e168-d4e6-4baa-91b7-c9846b9fc6f4'; // Axon fused to a dendrite (Ames, 2026-10-06), the 2D cut

/** "Sections" on: the 2D slice drawn inside the 3D view, which shows where
 *  the 2D images sit on the cell. */
export function showSections() {
  try { const v = getViewer()?.showPerspectiveSliceViews; if (v && !v.value) v.value = true; } catch (e) { /* */ }
}

/** Split view, so the 2D images are on screen for the 2D cut. */
export function show2D() {
  openSplitView();
}

/**
 * Tutorial 5: Cut. Split off the Cut & Merge tutorial on 2026-09-27 (Amy).
 * Practice cells: a `cut` example starts fused; "What is cut" previews the
 * finished cut (the two post-cut roots), "How to Cut" loads the fused root
 * for the learner to separate, and the wrap-up hands the cell back.
 */

export const steps: Step[] = [
  // 1: Welcome
  {
    title: "Cut",
    text: `
Sometimes the AI fuses two separate neurons into one. A <strong style="color:#e06060">cut</strong> separates them again.

If you see a segment with a branch that clearly belongs to a *different* cell, that's a cut waiting to happen. Here is one before and after:
`
    + beforeAfter(
        imgCutBefore, 'Before: a thin axon runs into this dendrite, and the AI made them one segment.',
        imgCutAfter, 'After a cut: the axon (yellow) is its own segment again.'),
    position: MIDDLE,
    width: "640px",
    nextLabel: "Let's learn!",
  },

  // 2: The finished cut, on the learner's own cell
  {
    title: "What a cut fixes",
    text: `
The yellow axon was incorrectly fused by AI to the purple dendrite. A citizen scientist like yourself corrected it by cutting it off.

In a moment you'll get the fused version of this very cell and make the cut yourself. It is yours alone until you finish.`,
    position: OVER_3D,
    width: "480px",
    onEnter: async () => {
      closeSidePanel();
      stopWatching();
      // Claim before loading any shared practice geometry.
      await movingToSandbox('Cut', async () => {
        const first = await beginPractice('cut', 'preview', { prefer: CUT_FIRST });
        // The second cut cell is taken now and shown at "Your Turn" (the
        // Merge tutorial does the same).
        if (first) await beginPractice('cut', 'start', { slot: 'b', show: false, prefer: CUT_SECOND });
        // The staged first cell has a finished cut to show. A stand-in cell
        // (when that one is busy) may never have been cut: say so rather
        // than call a fused cell a finished cut (Ames, 2026-10-05).
        if (first && !hasCutPreview(first)) {
          practiceStatus('This cell has not been cut before, so what you see is the fused version: two neurons as one purple segment. Press next and you will cut it.', true);
        }
      });
    },
  },

  // 3: Activating cut
  {
    title: "How to Cut",
    // A new player's Cut icon arrives here (util/toolbar_staging.ts).
    reveals: ['split'],
    text: `
Here's the same cell with the merger fused onto it. Your quest is to separate them.

Press the **C** key to start the cut tool, or click the red Cut icon in the top bar.

Once it's on, the cut bar appears at the bottom of the viewer with the red group active.`,
    position: OVER_3D,
    width: "440px",
    onEnter: async () => {
      closeSidePanel();
      // No "?" yet: the help it opens is about placing points, the next box.
      watchTool('Press C to start the cut tool.', 'Cut mode is on. Press next.', 'Cut mode is off. Press C again to re-enter cut mode.');
      await beginPractice('cut', 'start');
    },
  },

  // 4 to 6: the first cut, one small thing per box (Ames, 2026-10-09: the
  // single "Place the points" box was a lot of text). Each moves on by
  // itself when its one thing is done.
  // 4: a red point
  {
    title: "Place 2 red points",
    text: `
**Ctrl+Click** the piece that doesn't belong, once by each <strong style="color:#ff5c5c">red</strong> gem.

More than one point on each side tells the cut where the whole join is.`,
    position: OVER_3D,
    width: "380px",
    onEnter: async () => {
      closeSidePanel();
      stopWatching();
      await beginPractice('cut', 'start', { slot: 'a', prefer: holdsSlot('a') ? undefined : CUT_FIRST });
      // The second cell too, if it is not held (taken again after an idle release).
      if (!holdsSlot('b')) await beginPractice('cut', 'start', { slot: 'b', show: false, prefer: CUT_SECOND });
      // The previous step said "press C"; turn the tool on if they didn't.
      setTimeout(() => ensureTool('multicut'), 400);
      // Two red and two blue gems show where the points go. One point a
      // side made the cut take only half the axon off: every single-point
      // cut on this cell since 2026-10-06 left the far half attached, and
      // every two-point cut took all of it.
      setTimeout(() => { showWhereToCut(2); }, 1400);
      watchCutStep('red', 'Waiting for 2 red points: Ctrl+click by each red gem.', 2);
    },
  },

  // 5: switch colour
  {
    title: "Press G",
    text: `
Red is placed. Now press **G** to switch to <strong style="color:#5c8cff">blue</strong>.

The two colors mark the two sides of the cut.`,
    position: OVER_3D,
    width: "380px",
    onEnter: () => {
      closeSidePanel();
      watchCutStep('blue', 'Waiting for G: the bar at the bottom turns blue.');
    },
  },

  // 6: a blue point, and submit
  {
    title: "Place 2 blue points",
    text: `
**Ctrl+Click** the cell, once by each <strong style="color:#5c8cff">blue</strong> gem.

Then press **Submit cut** on the bar at the bottom, or **Enter**.`,
    position: OVER_3D,
    width: "380px",
    onEnter: async () => {
      closeSidePanel();
      watchPractice(false, 'Waiting for 2 blue points, then Submit cut.', '', { advance: true });
      await beginPractice('cut', 'start', { slot: 'a' });
      setTimeout(() => ensureTool('multicut'), 400);
    },
  },

  // 7: Cut success
  {
    title: "Cut success!",
    text: `
You did it. The piece that didn't belong is its own segment now.

` + BLACK_BOX_NOTE,
    position: OVER_3D,
    width: "420px",
    image: imgBravoNurro,
    onEnter: celebrateStep,
  },

  // 8: Cutting in 2D, then straight to the second cell
  {
    title: "You can also cut in 2D",
    text: `
**Ctrl+Click** places points on the 2D images too, not only on the 3D shape: 3 or 4 <strong style="color:#ff5c5c">red</strong> inside one cell's outline and 3 or 4 <strong style="color:#5c8cff">blue</strong> inside the other's, spread over a few slices. With one point a side the cut may take only part of the piece, or break off small extra pieces. It helps when the join is hard to see in 3D.

<span class="nge-cut-2d-next">Press next to try it on a new cell.</span>`,
    position: OVER_2D,
    width: "440px",
    onEnter: () => {
      closeSidePanel();
      stopWatching();
      // This box is about the 2D images, so put them on screen.
      show2D();
      // Only promise the 2D try when there is a second cell to try it on.
      setTimeout(() => {
        const el = document.querySelector('.nge-cut-2d-next');
        if (el && !holdsSlot('b')) el.textContent = 'A second example to try it on is coming soon.';
      }, 300);
    },
  },

  // 9: The 2D cut, on the second cell
  {
    title: "Your Turn: cut in 2D",
    text: `
A new cell, again with an axon (the thin branch) fused onto a dendrite (the thicker branch with all the little blobs coming out of it). Fun fact: each one of those blobs is the receiving half of a synapse!

Click to add new cut points in the 2D images on the left, on either side of where the two touch.

1. Press **C** if the cut tool is off.
2. **Ctrl+Click** 3 or 4 <strong style="color:#ff5c5c">red</strong> points inside the piece that doesn't belong.
3. Press **G**, then **Ctrl+Click** 3 or 4 <strong style="color:#5c8cff">blue</strong> points on the other side of the join.
4. Press **Submit cut** on the bar at the bottom, or **Enter**.

The red and blue dots on the images show where the points go.`,
    position: OVER_3D,
    width: "420px",
    onEnter: async () => {
      closeSidePanel();
      // No second cut cell: never show the first again (it is already cut),
      // go to the wrap up.
      if (!holdsSlot('b')) { const st = useTutorialStore(); if (st.getTutorialStep() === 8) st.setTutorialStep(9); return; }
      // The second cut cell, taken at the start together with the first. The
      // watch starts only once it is the cell on screen: started before, it
      // saw the first cell, already cut, and jumped straight to "done" while
      // this cell was still loading (Ames, 2026-10-06).
      stopWatching();
      await beginPractice('cut', 'start', { slot: 'b' });
      watchPractice(false, 'Waiting for your cut: red on one side of the join, G, blue on the other, Submit cut.', '', { advance: true });
      setTimeout(() => { show2D(); showSections(); }, 900);
      setTimeout(() => ensureTool('multicut'), 1400);
      // The example points show from the start here too (Ames): in 2D they
      // are dots on the images, which is where this cut is made.
      setTimeout(() => { showWhereToCut(); }, 1900);
    },
  },

  // 10: Done. Kept short (Ames: the box was taller than the screen); the full
  // key list is one click away.
  {
    title: "Cut: done!",
    text: `
<img src="` + imgBravoNurro + `" alt="" style="display:block;width:120px;height:auto;margin:0 auto 10px">

<span class="nge-done-lead">You now know the two most important proofreading operations: every <strong style="color:#60c060">merge</strong> reconnects a lost branch, every <strong style="color:#e06060">cut</strong> untangles confused neurons.</span>

The cells you practised on are put back for the next person. Happy proofreading!

Good, you've learned the basics of cut. Next is **Advanced Interface**: annotations, finding your way, working with many cells, and using **Find Path** to track down where a merger starts. After that, the Merger Sandbox's **${SANDBOX_TOTAL} mergers** put it all to work.

<div style="text-align:center;margin:16px 0 18px">` + startTutorialButton(2, 'Start Advanced Interface') + `</div>

<a href="` + CHEAT_SHEET_URL + `" target="_blank" rel="noopener" style="color:#7ecaff">Open the cheat sheet in a new tab ↗</a>`,
    position: MIDDLE,
    width: "460px",
    onEnter: () => {
      stopWatching();
      // Confetti only for cuts actually made.
      if (finishPracticeTutorial('cut')) document.dispatchEvent(new CustomEvent('nge:tutorial-celebrate'));
      endPractice();
    },
  },
];
